import React from 'react';

import Container from '@hero/ui-kit/layout/Container';
import Section from '@hero/ui-kit/layout/Section';
import Loading from '../../../../components/States/Loading';
import useGetMemberAIInsights, { AIInsightsResponse } from '../../api/useGetMemberAIInsights';
import { useOrganizationMemberDetailsContext } from '../context/OrganizationMemberDetailsContext';
import MemberDetailsWrapper from '../MemberDetailsWrapper';
import MarkdownSection from '../../../../components/MarkdownSection';
import InsightFeedback from './InsightFeedback';
import AdherenceSummaryCircle from './AdherenceSummaryCircle';
import AISection from '../../../../components/AISection';
import ClinicalSummary from './ClinicalSummary';
import MemberNotes from './MemberNotes';
import * as Style from './style.module.scss';

interface AIInsightsProps {
    data: AIInsightsResponse | undefined;
    isLoading: boolean;
    digestCallId: string;
}

const AIInsights: React.FC<AIInsightsProps> = ({ data, isLoading, digestCallId }) =>
    isLoading ? (
        <Section border>
            <Loading height={15} />
        </Section>
    ) : data ? (
        <AISection showHipaaBadge border paddingHorizontal="small">
            <InsightFeedback callId={digestCallId} copyText={data.digest} />
            <MarkdownSection markdown={data.digest} />
        </AISection>
    ) : null;

const AdherenceCircle: React.FC = () => {
    const { memberDetails, isDeviceConfigured } = useOrganizationMemberDetailsContext();
    const showAdherence =
        !!memberDetails?.member_info?.hero_user_id &&
        !!memberDetails?.medication_info?.length &&
        isDeviceConfigured;

    return showAdherence ? (
        <Section border noDefaultPadding paddingVertical="small">
            <AdherenceSummaryCircle />
        </Section>
    ) : null;
};

const OverviewContent: React.FC = () => {
    const { memberDetails } = useOrganizationMemberDetailsContext();
    // A member whose device isn't paired has no hero user, so no digest to fetch.
    const { data, isLoading, isError, digestCallId } = useGetMemberAIInsights(
        memberDetails?.member_info?.hero_user_id ?? null
    );
    const showInsights = isLoading || (!isError && !!data?.digest);

    // Without a digest the side cards move into the wide track rather than leave it empty.
    return (
        <Section paddingTop="small">
            <Container
                gridTemplateColumns={'2fr 1fr'}
                gridColumnGap="regular"
                styles={{ alignItems: 'start' }}
            >
                {showInsights ? (
                    <div className={Style.column}>
                        <AIInsights data={data} isLoading={isLoading} digestCallId={digestCallId} />
                    </div>
                ) : null}
                <div className={Style.column}>
                    <AdherenceCircle />
                    <MemberNotes />
                    <ClinicalSummary />
                </div>
            </Container>
        </Section>
    );
};

const OverviewPage: React.FC = () => (
    <MemberDetailsWrapper>
        <OverviewContent />
    </MemberDetailsWrapper>
);

export default OverviewPage;
